import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, Plus, Search, Send, Eye, CheckCircle2, Clock, 
  Building2, User, FileText, IndianRupee, Calendar, 
  Image, Video, Paperclip, X, Download, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DemandLettersPage = () => {
  const { user, hasRole, builder } = useAuth();
  const fileInputRef = useRef(null);
  
  const [demandLetters, setDemandLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    booking_id: '',
    milestone: '',
    milestone_name: '',
    amount_due: '',
    due_date: '',
    message: '',
    send_via: ['email'],
  });
  
  // Bulk form state
  const [bulkFormData, setBulkFormData] = useState({
    milestone: '',
    milestone_name: '',
    amount_due: '',
    due_date: '',
    message: '',
    send_via: ['email'],
  });

  const milestones = [
    { id: 'booking', name: 'Booking Amount' },
    { id: 'agreement', name: 'Agreement' },
    { id: 'slab_1', name: 'Slab 1 - Foundation' },
    { id: 'slab_2', name: 'Slab 2 - Plinth' },
    { id: 'slab_3', name: 'Slab 3 - Ground Floor' },
    { id: 'slab_4', name: 'Slab 4 - First Floor' },
    { id: 'slab_5', name: 'Slab 5 - Second Floor' },
    { id: 'slab_6', name: 'Slab 6 - Third Floor' },
    { id: 'slab_7', name: 'Slab 7 - Fourth Floor' },
    { id: 'slab_8', name: 'Slab 8 - Top Floor' },
    { id: 'plaster', name: 'Plastering Complete' },
    { id: 'flooring', name: 'Flooring Complete' },
    { id: 'possession', name: 'Possession' },
  ];

  useEffect(() => {
    loadDemandLetters();
    loadProjects();
    loadBookings();
  }, []);

  const loadDemandLetters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/demand-letters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDemandLetters(data);
      }
    } catch (error) {
      console.error('Failed to load demand letters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/leads?status=booked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array and paginated response
        const leads = Array.isArray(data) ? data : data.leads || [];
        setBookings(leads);
      }
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newAttachments = [];

    for (const file of files) {
      // Check file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        toast.error(`${file.name} is not an image or video`);
        continue;
      }

      // Check file size (max 50MB for videos, 10MB for images)
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max size: ${isVideo ? '50MB' : '10MB'}`);
        continue;
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);
      newAttachments.push({
        file,
        preview,
        name: file.name,
        type: isImage ? 'image' : 'video',
        size: file.size,
      });
    }

    setAttachments([...attachments, ...newAttachments]);
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    URL.revokeObjectURL(newAttachments[index].preview);
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const handleCreateDemandLetter = async () => {
    if (!formData.booking_id || !formData.milestone || !formData.amount_due || !formData.due_date) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // First create the demand letter
      const letterData = {
        ...formData,
        milestone_name: milestones.find(m => m.id === formData.milestone)?.name || formData.milestone,
      };

      const response = await fetch(`${API}/api/demand-letters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(letterData)
      });
      
      if (response.ok) {
        const letter = await response.json();
        
        // Upload attachments if any
        if (attachments.length > 0) {
          const formDataUpload = new FormData();
          attachments.forEach((att, idx) => {
            formDataUpload.append('files', att.file);
          });

          await fetch(`${API}/api/demand-letters/${letter.id}/attachments`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formDataUpload
          });
        }

        toast.success('Demand letter created and sent!');
        setDialogOpen(false);
        resetForm();
        loadDemandLetters();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create demand letter');
      }
    } catch (error) {
      toast.error('Failed to create demand letter');
    }
  };

  const resetForm = () => {
    setFormData({
      booking_id: '',
      milestone: '',
      milestone_name: '',
      amount_due: '',
      due_date: '',
      message: '',
      send_via: ['email'],
    });
    attachments.forEach(att => URL.revokeObjectURL(att.preview));
    setAttachments([]);
  };

  const resetBulkForm = () => {
    setBulkFormData({
      milestone: '',
      milestone_name: '',
      amount_due: '',
      due_date: '',
      message: '',
      send_via: ['email'],
    });
    setSelectedBookingIds([]);
  };

  const handleCreateBulkDemandLetters = async () => {
    if (selectedBookingIds.length === 0) {
      toast.error('Please select at least one buyer');
      return;
    }
    if (!bulkFormData.milestone || !bulkFormData.amount_due || !bulkFormData.due_date) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/demand-letters/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_ids: selectedBookingIds,
          milestone: bulkFormData.milestone,
          milestone_name: milestones.find(m => m.id === bulkFormData.milestone)?.name || bulkFormData.milestone,
          amount_due: bulkFormData.amount_due,
          due_date: bulkFormData.due_date,
          message: bulkFormData.message,
          send_via: bulkFormData.send_via,
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.created} demand letters created and sent!`);
        setBulkDialogOpen(false);
        resetBulkForm();
        loadDemandLetters();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create bulk demand letters');
      }
    } catch (error) {
      toast.error('Failed to create bulk demand letters');
    }
  };

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookingIds(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const selectAllBookings = () => {
    if (selectedBookingIds.length === bookings.length) {
      setSelectedBookingIds([]);
    } else {
      setSelectedBookingIds(bookings.map(b => b.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      viewed: 'bg-amber-100 text-amber-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${Number(amount).toLocaleString()}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredLetters = demandLetters.filter((letter) => {
    const matchesSearch = 
      letter.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      letter.unit_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      letter.milestone_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || letter.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const selectedBooking = bookings.find(b => b.id === formData.booking_id);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="demand-letters-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Mail className="h-7 w-7 text-amber-600" />
            Demand Letters
          </h1>
          <p className="text-muted-foreground mt-1">
            Send payment reminders to buyers based on construction milestones
          </p>
        </div>
        <div className="flex gap-2">
          {/* Bulk Send Dialog */}
          <Dialog open={bulkDialogOpen} onOpenChange={(open) => {
            setBulkDialogOpen(open);
            if (!open) resetBulkForm();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-bulk-demand-letter" disabled={bookings.length === 0}>
                <Send className="h-4 w-4 mr-2" />
                Bulk Send
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Send Bulk Demand Letters</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Select Buyers */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select Buyers ({selectedBookingIds.length} of {bookings.length} selected)</Label>
                    <Button variant="outline" size="sm" onClick={selectAllBookings}>
                      {selectedBookingIds.length === bookings.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {bookings.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No booked customers found</p>
                    ) : (
                      bookings.map((booking) => (
                        <div 
                          key={booking.id}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 ${
                            selectedBookingIds.includes(booking.id) ? 'bg-amber-50 border border-amber-200' : ''
                          }`}
                          onClick={() => toggleBookingSelection(booking.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBookingIds.includes(booking.id)}
                            onChange={() => toggleBookingSelection(booking.id)}
                            className="rounded"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{booking.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {booking.unit_number || 'Unit TBD'} • {booking.project_name}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Milestone */}
                <div className="space-y-2">
                  <Label>Payment Milestone *</Label>
                  <Select 
                    value={bulkFormData.milestone} 
                    onValueChange={(value) => setBulkFormData({...bulkFormData, milestone: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select milestone (e.g., Slab 3)" />
                    </SelectTrigger>
                    <SelectContent>
                      {milestones.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Amount and Due Date */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount Due (₹) *</Label>
                    <Input
                      type="number"
                      value={bulkFormData.amount_due}
                      onChange={(e) => setBulkFormData({...bulkFormData, amount_due: e.target.value})}
                      placeholder="e.g., 500000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date *</Label>
                    <Input
                      type="date"
                      value={bulkFormData.due_date}
                      onChange={(e) => setBulkFormData({...bulkFormData, due_date: e.target.value})}
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label>Custom Message (optional)</Label>
                  <Textarea
                    value={bulkFormData.message}
                    onChange={(e) => setBulkFormData({...bulkFormData, message: e.target.value})}
                    placeholder="Add a personalized message to all buyers..."
                    rows={3}
                  />
                </div>

                {/* Send Via */}
                <div className="space-y-2">
                  <Label>Send Via</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkFormData.send_via.includes('email')}
                        onChange={(e) => {
                          const newSendVia = e.target.checked 
                            ? [...bulkFormData.send_via, 'email']
                            : bulkFormData.send_via.filter(v => v !== 'email');
                          setBulkFormData({...bulkFormData, send_via: newSendVia});
                        }}
                        className="rounded"
                      />
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkFormData.send_via.includes('whatsapp')}
                        onChange={(e) => {
                          const newSendVia = e.target.checked 
                            ? [...bulkFormData.send_via, 'whatsapp']
                            : bulkFormData.send_via.filter(v => v !== 'whatsapp');
                          setBulkFormData({...bulkFormData, send_via: newSendVia});
                        }}
                        className="rounded"
                      />
                      <Phone className="h-4 w-4" />
                      WhatsApp
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateBulkDemandLetters} disabled={selectedBookingIds.length === 0}>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {selectedBookingIds.length} Buyer{selectedBookingIds.length !== 1 ? 's' : ''}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Single Create Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="btn-create-demand-letter">
                <Plus className="h-4 w-4 mr-2" />
                Create Demand Letter
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Demand Letter</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Select Booking/Buyer */}
              <div className="space-y-2">
                <Label>Select Buyer/Booking *</Label>
                <Select 
                  value={formData.booking_id} 
                  onValueChange={(value) => setFormData({...formData, booking_id: value})}
                >
                  <SelectTrigger data-testid="select-booking">
                    <SelectValue placeholder="Select booked customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {bookings.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        No bookings found
                      </div>
                    ) : (
                      bookings.map((booking) => (
                        <SelectItem key={booking.id} value={booking.id}>
                          {booking.name} - {booking.unit_number || 'Unit TBD'} ({booking.project_name})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {selectedBooking && (
                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2">Buyer Details</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> {selectedBooking.name}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {selectedBooking.phone}</div>
                      <div><span className="text-muted-foreground">Email:</span> {selectedBooking.email || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Unit:</span> {selectedBooking.unit_number || 'TBD'}</div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Milestone */}
              <div className="space-y-2">
                <Label>Payment Milestone *</Label>
                <Select 
                  value={formData.milestone} 
                  onValueChange={(value) => setFormData({...formData, milestone: value})}
                >
                  <SelectTrigger data-testid="select-milestone">
                    <SelectValue placeholder="Select milestone (e.g., Slab 3)" />
                  </SelectTrigger>
                  <SelectContent>
                    {milestones.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount and Due Date */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount Due (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.amount_due}
                    onChange={(e) => setFormData({...formData, amount_due: e.target.value})}
                    placeholder="e.g., 500000"
                    data-testid="input-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    data-testid="input-due-date"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label>Custom Message</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Add a personalized message to the buyer..."
                  rows={3}
                />
              </div>

              {/* Attachments - Construction Progress */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attach Construction Progress (Photos/Videos)
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="text-center">
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : (
                        <>
                          <Image className="h-4 w-4 mr-2" />
                          Add Photos/Videos
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Images up to 10MB, Videos up to 50MB
                    </p>
                  </div>

                  {/* Attachment Previews */}
                  {attachments.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {attachments.map((att, index) => (
                        <div key={index} className="relative group">
                          {att.type === 'image' ? (
                            <img 
                              src={att.preview} 
                              alt={att.name}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-20 bg-muted rounded-lg flex items-center justify-center">
                              <Video className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <p className="text-xs text-center truncate mt-1">{att.name}</p>
                          <p className="text-xs text-center text-muted-foreground">{formatFileSize(att.size)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Send Via */}
              <div className="space-y-2">
                <Label>Send Via</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.send_via.includes('email')}
                      onChange={(e) => {
                        const newSendVia = e.target.checked 
                          ? [...formData.send_via, 'email']
                          : formData.send_via.filter(v => v !== 'email');
                        setFormData({...formData, send_via: newSendVia});
                      }}
                      className="rounded"
                    />
                    <Mail className="h-4 w-4" />
                    Email
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.send_via.includes('whatsapp')}
                      onChange={(e) => {
                        const newSendVia = e.target.checked 
                          ? [...formData.send_via, 'whatsapp']
                          : formData.send_via.filter(v => v !== 'whatsapp');
                        setFormData({...formData, send_via: newSendVia});
                      }}
                      className="rounded"
                    />
                    <Phone className="h-4 w-4" />
                    WhatsApp
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateDemandLetter} data-testid="btn-submit-demand-letter">
                <Send className="h-4 w-4 mr-2" />
                Create & Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{demandLetters.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Send className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{demandLetters.filter(d => d.status === 'sent').length}</p>
                <p className="text-sm text-muted-foreground">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Eye className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{demandLetters.filter(d => d.status === 'viewed').length}</p>
                <p className="text-sm text-muted-foreground">Viewed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{demandLetters.filter(d => d.status === 'paid').length}</p>
                <p className="text-sm text-muted-foreground">Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Clock className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{demandLetters.filter(d => d.status === 'overdue').length}</p>
                <p className="text-sm text-muted-foreground">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by buyer name, unit, or milestone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="input-search-demand-letters"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="viewed">Viewed</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Demand Letters List */}
      {filteredLetters.length === 0 ? (
        <Card className="p-8 text-center">
          <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Demand Letters Found</h3>
          <p className="text-muted-foreground mb-4">
            Create demand letters to remind buyers about milestone payments.
          </p>
          {bookings.length > 0 && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Demand Letter
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredLetters.map((letter) => (
            <Card key={letter.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <Mail className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{letter.buyer_name}</h3>
                        {getStatusBadge(letter.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">{letter.milestone_name}</span>
                        {letter.unit_number && ` • Unit: ${letter.unit_number}`}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {letter.project_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due: {new Date(letter.due_date).toLocaleDateString()}
                        </span>
                        {letter.attachments_count > 0 && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Paperclip className="h-3 w-3" />
                            {letter.attachments_count} attachment(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-amber-600">{formatCurrency(letter.amount_due)}</p>
                      <p className="text-xs text-muted-foreground">Amount Due</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedLetter(letter);
                        setViewDialogOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Demand Letter Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Demand Letter Details</DialogTitle>
          </DialogHeader>
          {selectedLetter && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex justify-between items-start p-4 bg-muted/30 rounded-lg">
                <div>
                  <h3 className="text-xl font-bold">{selectedLetter.milestone_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Sent: {new Date(selectedLetter.created_at).toLocaleDateString()}
                  </p>
                </div>
                {getStatusBadge(selectedLetter.status)}
              </div>

              {/* Buyer Details */}
              <div className="p-4 border rounded-lg">
                <p className="text-xs uppercase text-muted-foreground mb-2">Buyer Details</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Name:</span> {selectedLetter.buyer_name}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selectedLetter.buyer_phone}</div>
                  <div><span className="text-muted-foreground">Email:</span> {selectedLetter.buyer_email || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Unit:</span> {selectedLetter.unit_number}</div>
                  <div><span className="text-muted-foreground">Project:</span> {selectedLetter.project_name}</div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm text-muted-foreground">Amount Due</span>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency(selectedLetter.amount_due)}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground">Due Date</span>
                    <p className="text-lg font-medium">{new Date(selectedLetter.due_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedLetter.message && (
                <div className="p-4 border rounded-lg">
                  <p className="text-xs uppercase text-muted-foreground mb-2">Message</p>
                  <p className="text-sm">{selectedLetter.message}</p>
                </div>
              )}

              {/* Attachments */}
              {selectedLetter.attachments && selectedLetter.attachments.length > 0 && (
                <div className="p-4 border rounded-lg">
                  <p className="text-xs uppercase text-muted-foreground mb-2">Construction Progress Attachments</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedLetter.attachments.map((att, idx) => (
                      <div key={idx} className="relative">
                        {att.type === 'image' ? (
                          <img 
                            src={att.url} 
                            alt={`Attachment ${idx + 1}`}
                            className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80"
                            onClick={() => window.open(att.url, '_blank')}
                          />
                        ) : (
                          <div 
                            className="w-full h-24 bg-muted rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/80"
                            onClick={() => window.open(att.url, '_blank')}
                          >
                            <Video className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <DialogFooter className="gap-2">
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button variant="outline">
                  <Send className="h-4 w-4 mr-2" />
                  Resend
                </Button>
                {selectedLetter.status !== 'paid' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await fetch(`${API}/api/demand-letters/${selectedLetter.id}/status`, {
                          method: 'PATCH',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                          },
                          body: JSON.stringify({ status: 'paid' })
                        });
                        toast.success('Marked as paid');
                        loadDemandLetters();
                        setViewDialogOpen(false);
                      } catch (error) {
                        toast.error('Failed to update status');
                      }
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemandLettersPage;
